import React, { useMemo, useState, useEffect, useRef } from 'react';

import BlockGlossaryBar from '../BlockGlossaryBar';
import cls from '@soluto-design/styles/cls';
import BlockGlossary, { BlockGlossaryBaseProps } from '../BlockGlossary';

export interface GlossarySectionProps {
    letters: BlockGlossaryBaseProps[];
    showMoreLabel?: string;
}

const GlossarySection: React.FC<GlossarySectionProps> = ({
    letters,
    showMoreLabel,
}) => {
    const glossaryBarRef = useRef<HTMLDivElement>(null);
    const glossaryContainerRef = useRef<HTMLDivElement>(null);

    const [headerHeight, setHeaderHeight] = useState<number>(0);
    const [glossaryBarHeight, setGlossaryBarHeight] = useState<number>(0);
    const [isPinned, setIsPinned] = useState<boolean>(false);
    const [letterSelected, setLetterSelected] = useState<string | null>(null);

    const tags = useMemo(() => {
        return letters.map(({ letter, items }) => {
            return {
                label: letter,
                active: letter === letterSelected,
                disabled: !items?.length,
            };
        });
    }, [letters, letterSelected]);

    useEffect(() => {
        if (typeof window === 'undefined' || !glossaryBarRef?.current) {
            return;
        }

        const header = document.querySelector('.transition-header');
        if (header) {
            const rect = header.getBoundingClientRect();
            setHeaderHeight(rect.height);
        }

        if (glossaryBarRef.current) {
            setGlossaryBarHeight(glossaryBarRef.current.clientHeight);
        }
    }, []);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            !glossaryBarRef?.current ||
            typeof IntersectionObserver === 'undefined'
        ) {
            return;
        }

        const ref = glossaryBarRef.current;

        const header = document.querySelector('header');

        const headerHeight = header?.getBoundingClientRect().height ?? 0;

        const offsetY = headerHeight + 16;

        const observer = new IntersectionObserver(
            ([e]) => {
                const pinned = e.intersectionRatio < 1;
                setIsPinned(pinned);
                ref.dataset.pinned = pinned ? 'true' : 'false';
            },
            {
                threshold: [1],
                rootMargin: `-${offsetY}px 0px 0px 0px`,
            },
        );

        observer.observe(ref);

        const mutationObserver = new MutationObserver((mutationsList) => {
            for (let mutation of mutationsList) {
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'data-pinned'
                ) {
                    setIsPinned(mutation.target.dataset.pinned === 'true');
                }
            }
        });

        if (glossaryBarRef.current) {
            mutationObserver.observe(glossaryBarRef.current, {
                attributes: true,
            });
        }

        return () => {
            if (observer && ref) {
                observer.unobserve(ref);
            }
            mutationObserver.disconnect();
        };
    }, []);

    return (
        <section className="mt-[calc(var(--soluto-spacing-xl)-32px)] mb-xxxl">
            <div
                ref={glossaryBarRef}
                style={{ top: `${headerHeight}px` }}
                className={cls('sticky z-10 mb-xxxl pt-32')}>
                <BlockGlossaryBar
                    uppercase
                    isPinned={isPinned}
                    tags={tags}
                    onClick={(category) => {
                        const letter = category?.label;

                        const section = document.querySelector(
                            `[data-letter-id="${letter}"]`,
                        );

                        if (section) {
                            setLetterSelected(letter);

                            section.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                            });
                        }
                    }}
                />
            </div>
            <div
                ref={glossaryContainerRef}
                className="container flex flex-col gap-y-xl">
                {letters?.length > 0 &&
                    letters
                        .filter(({ items }) => !!items.length)
                        .map((props, i) => {
                            return (
                                <BlockGlossary
                                    key={`block-glossary-${i}`}
                                    {...props}
                                    topHeight={glossaryBarHeight + headerHeight}
                                    showMoreLabel={showMoreLabel}
                                />
                            );
                        })}
            </div>
        </section>
    );
};

export default GlossarySection;
